import { useState } from 'react';
import HorseInfoCard from './cards/HorseInfoCard';
import HorseTrainingCard from './cards/HorseTrainingCard';
import HorseDailyCareCard from './cards/HorseDailyCareCard';
import HorseInteractiveCard from './cards/HorseInteractiveCard';
import { THEME_COLORS } from '../../constants/horseConstants';

/**
 * Horse Card Component
 * Displays a 4-card stack (Info, Training, Daily Care, Interactive) for a single horse
 * @param {object} mustang - The mustang data
 * @param {function} onEdit - Callback to edit the horse
 * @param {function} onDelete - Callback to delete the horse
 * @param {function} onGentlingChange - Callback for training/gentling checkbox changes
 * @param {function} onDailyCareChange - Callback for daily care checkbox changes
 * @param {function} onSpriteChange - Callback when sprite is changed
 * @param {function} onFeed - Callback when feed button is clicked
 * @param {function} onWater - Callback when water button is clicked
 * @param {function} onGroom - Callback when groom button is clicked
 * @param {function} onExercise - Callback when exercise button is clicked
 * @param {array} dailyCareCategories - Custom daily care categories (optional)
 * @param {array} trainingCategories - Custom training categories (optional)
 * @param {boolean} isAdmin - Whether the user is an admin (enables preview mode)
 */
const HorseCard = ({ 
  mustang, 
  onEdit, 
  onDelete, 
  onGentlingChange, 
  onDailyCareChange,
  onSpriteChange,
  onFeed,
  onWater,
  onGroom,
  onExercise,
  dailyCareCategories,
  trainingCategories,
  isAdmin = false
}) => {
  const [activeCard, setActiveCard] = useState('info');

  return (
    <div className="relative" style={{ minHeight: '600px' }}>
      {/* Interactive Card - Back of stack (4th position) */}
      <div
        className="absolute border-2 rounded-xl p-3 bg-white transition-all duration-300 overflow-visible"
        style={{
          borderColor: THEME_COLORS.primary, // Orange for interactive
          top: activeCard === 'interactive' ? '0' : '36px',
          left: activeCard === 'interactive' ? '0' : '36px',
          right: activeCard === 'interactive' ? '0' : '-36px',
          bottom: activeCard === 'interactive' ? '0' : '-36px',
          zIndex: activeCard === 'interactive' ? 30 : 2,
          boxShadow: activeCard === 'interactive' ? '0 10px 25px rgba(0,0,0,0.15)' : '0 4px 6px rgba(0,0,0,0.1)'
        }}
      >
        {/* Interactive Tab - Positioned further left to be visible */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            setActiveCard('interactive');
          }}
          className="absolute cursor-pointer hover:brightness-110 hover:scale-105 transition-all"
          style={{
            right: '195px',
            top: '-56px',
            height: '56px',
            paddingLeft: '14px',
            paddingRight: '14px',
            paddingTop: '8px',
            backgroundColor: activeCard === 'interactive' ? THEME_COLORS.primary : '#E08B6F',
            borderRadius: '8px 8px 0 0',
            boxShadow: activeCard === 'interactive' ? '0 -2px 8px rgba(0,0,0,0.15)' : '0 -1px 4px rgba(0,0,0,0.1)',
            border: activeCard === 'interactive' ? 'none' : '2px solid rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 9999,
            opacity: 1
          }}
          data-testid="interactive-tab"
        >
          <span className="text-white font-bold text-sm whitespace-nowrap flex items-center gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Play
          </span>
        </div>

        {/* Card Content */}
        <div className="pt-2 overflow-y-auto" style={{ maxHeight: '550px' }}>
          <HorseInteractiveCard
            mustang={mustang}
            onSpriteChange={(spriteId) => onSpriteChange && onSpriteChange(mustang.id, spriteId)}
            onFeed={() => onFeed && onFeed(mustang.id)}
            onWater={() => onWater && onWater(mustang.id)}
            onGroom={() => onGroom && onGroom(mustang.id)}
            onExercise={() => onExercise && onExercise(mustang.id)}
            isAdmin={isAdmin}
          />
        </div>
      </div>

      {/* Daily Care Card - Back of stack (3rd position) */}
      <div
        className="absolute border-2 rounded-xl p-3 bg-white transition-all duration-300 overflow-visible"
        style={{
          borderColor: THEME_COLORS.secondary,
          top: activeCard === 'daily' ? '0' : '24px',
          left: activeCard === 'daily' ? '0' : '24px',
          right: activeCard === 'daily' ? '0' : '-24px',
          bottom: activeCard === 'daily' ? '0' : '-24px',
          zIndex: activeCard === 'daily' ? 30 : 5,
          boxShadow: activeCard === 'daily' ? '0 10px 25px rgba(0,0,0,0.15)' : '0 4px 6px rgba(0,0,0,0.1)'
        }}
      >
        {/* Daily Care Tab */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            setActiveCard('daily');
          }}
          className="absolute cursor-pointer hover:brightness-110 hover:scale-105 transition-all"
          style={{
            right: '135px',
            top: '-48px',
            height: '48px',
            paddingLeft: '10px',
            paddingRight: '10px',
            paddingTop: '6px',
            backgroundColor: activeCard === 'daily' ? THEME_COLORS.secondary : '#9FE0E3',
            borderRadius: '8px 8px 0 0',
            boxShadow: activeCard === 'daily' ? '0 -2px 8px rgba(0,0,0,0.15)' : '0 -1px 4px rgba(0,0,0,0.1)',
            border: activeCard === 'daily' ? 'none' : '2px solid rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 9999,
            opacity: 1
          }}
          data-testid="daily-care-tab"
        >
          <span className="text-white font-bold text-xs whitespace-nowrap flex items-center gap-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_mustanglife/artifacts/mk9pxe7f_1604FCBE-AA96-40EC-A37E-2F9FE9F13B1F.png" 
              alt="Daily Care" 
              className="w-4 h-4 object-contain"
            />
            Care
          </span>
        </div>

        {/* Card Content */}
        <div className="pt-2 overflow-y-auto" style={{ maxHeight: '550px' }}>
          <HorseDailyCareCard
            mustang={mustang}
            onCheckboxChange={onDailyCareChange}
            categories={dailyCareCategories}
          />
        </div>
      </div>

      {/* Training Card - Middle of stack (2nd position) */}
      <div
        className="absolute border-2 rounded-xl p-3 bg-white transition-all duration-300 overflow-visible"
        style={{
          borderColor: THEME_COLORS.primary,
          top: activeCard === 'training' ? '0' : '12px',
          left: activeCard === 'training' ? '0' : '12px',
          right: activeCard === 'training' ? '0' : '-12px',
          bottom: activeCard === 'training' ? '0' : '-12px',
          zIndex: activeCard === 'training' ? 30 : 10,
          boxShadow: activeCard === 'training' ? '0 10px 25px rgba(0,0,0,0.15)' : '0 4px 6px rgba(0,0,0,0.1)'
        }}
      >
        {/* Training Tab */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            setActiveCard('training');
          }}
          className="absolute cursor-pointer hover:brightness-110 hover:scale-105 transition-all"
          style={{
            right: '75px',
            top: '-48px',
            height: '48px',
            paddingLeft: '12px',
            paddingRight: '12px',
            paddingTop: '6px',
            backgroundColor: activeCard === 'training' ? THEME_COLORS.primary : '#E08B6F',
            borderRadius: '8px 8px 0 0',
            boxShadow: activeCard === 'training' ? '0 -2px 8px rgba(0,0,0,0.15)' : '0 -1px 4px rgba(0,0,0,0.1)',
            border: activeCard === 'training' ? 'none' : '2px solid rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 9999,
            opacity: 1
          }}
          data-testid="training-tab"
        >
          <span className="text-white font-bold text-xs whitespace-nowrap">
            Train
          </span>
        </div>

        {/* Card Content */}
        <div className="pt-2 overflow-y-auto" style={{ maxHeight: '550px' }}>
          <HorseTrainingCard
            mustang={mustang}
            onCheckboxChange={onGentlingChange}
            categories={trainingCategories}
          />
        </div>
      </div>

      {/* Info Card - Front of stack (1st position) */}
      <div
        className="absolute border-2 rounded-xl p-3 bg-white transition-all duration-300 overflow-visible"
        style={{
          borderColor: mustang.is_real_adoption ? '#372F2F' : THEME_COLORS.secondary,
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          zIndex: activeCard === 'info' ? 30 : 20,
          boxShadow: activeCard === 'info' ? '0 10px 25px rgba(0,0,0,0.15)' : '0 4px 6px rgba(0,0,0,0.1)'
        }}
      >
        {/* Info Tab */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            setActiveCard('info');
          }}
          className="absolute cursor-pointer hover:brightness-110 hover:scale-105 transition-all"
          style={{
            right: '15px',
            top: '-48px',
            height: '48px',
            paddingLeft: '14px',
            paddingRight: '14px',
            paddingTop: '6px',
            backgroundColor: activeCard === 'info'
              ? (mustang.is_real_adoption ? '#372F2F' : THEME_COLORS.secondary)
              : (mustang.is_real_adoption ? '#6B6260' : '#9FE0E3'),
            borderRadius: '8px 8px 0 0',
            boxShadow: activeCard === 'info' ? '0 -2px 8px rgba(0,0,0,0.15)' : '0 -1px 4px rgba(0,0,0,0.1)',
            border: activeCard === 'info' ? 'none' : '2px solid rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 9999,
            opacity: 1
          }}
          data-testid="info-tab"
        >
          <span className="text-white font-bold text-xs whitespace-nowrap">
            Info
          </span>
        </div>

        {/* Horse Name Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold" style={{ color: THEME_COLORS.primary }}>
              {mustang.name}
            </h3>
            {mustang.is_real_adoption && (
              <span className="text-xl" title="Real Mustang">⭐</span>
            )}
          </div>

          {/* Edit and Delete Buttons */}
          <div className="flex gap-1">
            <div
              onClick={() => onEdit(mustang)}
              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-full transition-all cursor-pointer"
              title="Edit horse"
              data-testid="edit-horse-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
            </div>
            <div
              onClick={() => onDelete(mustang.id, mustang.name)}
              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-full transition-all cursor-pointer"
              title="Remove horse"
              data-testid="delete-horse-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        {/* Card Content */}
        <div className="overflow-y-auto" style={{ maxHeight: '500px' }}>
          <HorseInfoCard mustang={mustang} onEdit={onEdit} onDelete={onDelete} />
        </div>
      </div>
    </div>
  );
};

export default HorseCard;
